import { useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { Player } from "@/lib/types";
import { getCoachingNotes, errorMessage } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import { useMe } from "@/features/auth/useMe";
import {
  useCoachUsers,
  useRoster,
  useRosterMutations,
} from "./hooks/useRoster";
import {
  computeRosterKpis,
  filterRoster,
  formatNotesPerPlayer,
  type RosterStatusFilter,
} from "./rosterMetrics";
import { PlayerModal } from "./components/PlayerModal";
import { LinkAccountModal } from "./components/LinkAccountModal";
import { LinkAccountPickerModal } from "./components/LinkAccountPickerModal";
import { RosterImportModal } from "./components/RosterImportModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";

/**
 * Coach > Roster list page.
 *
 * Ports the legacy `renderCoachRoster` surface
 * (`js/coaching/roster.js`): KPI tiles, a search box + status
 * filter, the player table with link chips + icon row actions, and
 * an always-visible "Quick Add Player" sidebar form.
 *
 * Layout (restored from the legacy two-pane design):
 *   - Header — descriptive title + `Import CSV` (team-admin gated)
 *     and `Add Player` (focuses the Quick Add form).
 *   - Two-pane body — the roster table on the left, a persistent
 *     `Quick Add Player` form on the right. Quick Add is a true
 *     inline form, NOT a modal: name + jersey + position + an
 *     optional account link, one `Add to Roster` button. The full
 *     `PlayerModal` is still used for detailed EDITS (per-row
 *     pencil) where the active toggle and longer fields matter.
 *
 * Row actions are compact ICON buttons (observation / development /
 * goal / link / edit / delete) — text labels live in `title` +
 * `aria-label` so the row stays scannable.
 *
 * KPIs reflect the FULL roster (not the filtered view) — legacy
 * behavior. The notes-per-player KPI needs every team note, so
 * this page also reads `getCoachingNotes()` (unfiltered).
 */

export type RosterPageProps = {
  /** Opens the goal composer (create mode) preselected to this
   * player. Undefined ⇒ the row's goal button is hidden. Owned by
   * `CoachShell` so the goal + development modals stay
   * one-at-a-time. */
  onCreatePlayerGoal?: (playerId: string) => void;
  /** Opens the Player Development modal for a player. Owned by
   * `CoachShell` (same one-modal-at-a-time reason). */
  onOpenDevelopment: (playerId: string, playerName: string) => void;
};

const STATUS_FILTERS: ReadonlyArray<{
  value: RosterStatusFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/** Common positions for the Quick Add form. The roster data model
 * has no `position` column — the legacy roster overloads the
 * free-text `notes` field as the position, so these write straight
 * into `notes`. An empty value leaves `notes` blank. */
const POSITION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "—" },
  { value: "Goalkeeper", label: "Goalkeeper" },
  { value: "Defender", label: "Defender" },
  { value: "Midfielder", label: "Midfielder" },
  { value: "Forward", label: "Forward" },
];

export function RosterPage({
  onCreatePlayerGoal,
  onOpenDevelopment,
}: RosterPageProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe().data;
  const teamId = useScopeStore((s) => s.teamId);
  // CSV roster import is a roster mutation — the backend gates both
  // import endpoints on the `roster:manage` capability (coach,
  // team_admin, global_admin; NOT assistant_coach). Gate the launch
  // button on the same capability so a coach sees it but an
  // assistant coach — who has read-only roster access — does not.
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const canImportRoster =
    isGlobalAdmin ||
    !!me?.active_scope?.capabilities?.includes("roster:manage");
  const rosterQuery = useRoster();
  const mutations = useRosterMutations();

  // The notes-per-player KPI needs every team note (unfiltered).
  const notesQuery = useQuery({
    queryKey: useScopedKey("coaching-notes", { match_id: null }),
    queryFn: () => getCoachingNotes(),
    enabled: !!teamId,
  });

  const players = useMemo(
    () => rosterQuery.data?.players ?? [],
    [rosterQuery.data],
  );
  const notes = useMemo(
    () => notesQuery.data?.notes ?? [],
    [notesQuery.data],
  );
  const kpis = useMemo(
    () => computeRosterKpis(players, notes),
    [players, notes],
  );

  // ---- Search + status filter ----
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<RosterStatusFilter>("all");
  const filtered = useMemo(
    () => filterRoster(players, search, statusFilter),
    [players, search, statusFilter],
  );

  // ---- Modal state ----
  // `editModal`: the player being edited (full PlayerModal). Quick
  // Add is the inline sidebar form, not a modal. `linkModal`: the
  // player being linked. `linkPickerOpen`: the header "Link Account"
  // flow — a player-picker that, on continue, populates `linkModal`.
  const [editModal, setEditModal] = useState<Player | null>(null);
  const [linkModal, setLinkModal] = useState<Player | null>(null);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Player | null>(null);

  // The Quick Add sidebar form's name input — the header "Add
  // Player" button focuses it so the entry point still reads as a
  // top-level action.
  const quickAddNameRef = useRef<HTMLInputElement | null>(null);
  const focusQuickAdd = () => {
    quickAddNameRef.current?.focus();
    quickAddNameRef.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  };

  const handleAddObservation = (playerId: string) => {
    // The M4→M6 carry-in: route to Coach Review's tactical-board
    // surface with the player preselected. Coach Review reads
    // `?source` + `?player_id` from the search params.
    navigate({
      to: "/coach/review",
      search: { source: "tactical_board", player_id: playerId },
    });
  };

  const confirmDeletePlayer = async () => {
    if (!confirmDelete) return;
    const player = confirmDelete;
    setConfirmDelete(null);
    setDeleting(player.id);
    try {
      await mutations.deletePlayer(player.id);
      toast.success("Player deleted");
    } catch (err) {
      toast.error("Couldn't delete player", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="roster-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete player?"
          message={`Delete ${confirmDelete.display_name}? This removes the player and their account links.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeletePlayer}
          onClose={() => setConfirmDelete(null)}
          data-testid="roster-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Roster & Family Links"
        description="Manage players on the team, jersey numbers, and the family or player accounts that can see private feedback."
        actions={
          <>
            {canImportRoster ? (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                data-testid="roster-import-open"
                className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
              >
                <ImportIcon />
                Import CSV
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setLinkPickerOpen(true)}
              data-testid="roster-link-open"
              className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
            >
              <LinkIcon />
              Link Account
            </button>
            <button
              type="button"
              onClick={focusQuickAdd}
              data-testid="roster-add-player"
              className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-xs font-display uppercase tracking-wider text-white hover:bg-accent-hover transition-colors"
            >
              <PlusIcon />
              Add Player
            </button>
          </>
        }
      />

      {/* KPI tiles — reflect the full roster, not the filtered view. */}
      <div
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="roster-kpis"
      >
        <KpiTile label="Active Players" value={String(kpis.activePlayers)} />
        <KpiTile
          label="Linked Accounts"
          value={String(kpis.linkedAccounts)}
          delta={
            kpis.linkedAccountsThisWeek > 0
              ? `+${kpis.linkedAccountsThisWeek} this week`
              : undefined
          }
        />
        <KpiTile
          label="Without Family Link"
          value={String(kpis.withoutLink)}
        />
        <KpiTile
          label="Avg. Notes / Player"
          value={formatNotesPerPlayer(kpis.notesPerPlayer)}
        />
      </div>

      {/* Toolbar: search + status filter. */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, jersey, position, linked account…"
          data-testid="roster-search"
          className="flex-1 min-w-[14rem] bg-bg-card border border-rule rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <div
          className="flex gap-1 rounded-md border border-rule bg-bg-card p-1"
          role="group"
          aria-label="Status filter"
        >
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              data-roster-filter={f.value}
              data-active={statusFilter === f.value}
              className={[
                "rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors",
                statusFilter === f.value
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Two-pane body: roster table on the left, the always-visible
       * Quick Add Player form on the right. The table gets the full
       * width until `xl` so mid-width laptops/tablets don't squeeze
       * row actions into tall wrapped stacks. */}
      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1">
          {rosterQuery.isLoading ? (
            <p
              className="text-ink-muted text-sm font-mono px-3 py-6"
              data-testid="roster-loading"
            >
              Loading roster…
            </p>
          ) : players.length === 0 ? (
            <p
              className="text-ink-muted text-sm px-3 py-6"
              data-testid="roster-empty"
            >
              No roster players yet. Use the <strong>Quick Add Player</strong>{" "}
              form to add the first one.
            </p>
          ) : filtered.length === 0 ? (
            <p
              className="text-ink-muted text-sm px-3 py-6"
              data-testid="roster-no-match"
            >
              No players match your search or filter.{" "}
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                }}
                className="text-accent hover:underline"
              >
                Clear filters
              </button>
            </p>
          ) : (
            <>
            <div className="hidden overflow-x-auto rounded-lg border border-rule bg-bg-card md:block">
              <table className="min-w-[54rem] w-full text-sm" data-testid="roster-table">
                <thead>
                  <tr className="text-left text-ink-muted text-xs font-display uppercase tracking-wider border-b border-rule">
                    <th className="px-3 py-2 w-12">#</th>
                    <th className="px-3 py-2">Player</th>
                    <th className="px-3 py-2">Linked accounts</th>
                    <th className="px-3 py-2 w-24">Status</th>
                    {/* PR 8: "How'd it land?" signal aggregate per
                     * player — small chip strip showing recent
                     * Got it / Need help / I tried this counts so
                     * the coach can spot at a glance who responded
                     * + who needs follow-up. Hidden in the empty
                     * state via the `signal_summary` guard on the
                     * cell. */}
                    <th className="px-3 py-2 w-32">Responses</th>
                    <th className="px-3 py-2 w-[14rem] whitespace-nowrap text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <RosterRow
                      key={p.id}
                      player={p}
                      deleting={deleting === p.id}
                      onAddObservation={() => handleAddObservation(p.id)}
                      onOpenDevelopment={() =>
                        onOpenDevelopment(p.id, p.display_name)
                      }
                      onCreateGoal={
                        onCreatePlayerGoal
                          ? () => onCreatePlayerGoal(p.id)
                          : undefined
                      }
                      onLink={() => setLinkModal(p)}
                      onUnlink={async (linkId) => {
                        try {
                          await mutations.unlinkAccount(linkId);
                          toast.success("Account unlinked");
                        } catch (err) {
                          toast.error("Couldn't unlink account", {
                            description: errorMessage(err),
                          });
                        }
                      }}
                      onEdit={() => setEditModal(p)}
                      onDelete={() => setConfirmDelete(p)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div
              className="flex flex-col gap-2 md:hidden"
              data-testid="roster-card-list"
            >
              {filtered.map((p) => (
                <RosterCard
                  key={p.id}
                  player={p}
                  deleting={deleting === p.id}
                  onAddObservation={() => handleAddObservation(p.id)}
                  onOpenDevelopment={() =>
                    onOpenDevelopment(p.id, p.display_name)
                  }
                  onCreateGoal={
                    onCreatePlayerGoal ? () => onCreatePlayerGoal(p.id) : undefined
                  }
                  onLink={() => setLinkModal(p)}
                  onUnlink={async (linkId) => {
                    try {
                      await mutations.unlinkAccount(linkId);
                      toast.success("Account unlinked");
                    } catch (err) {
                      toast.error("Couldn't unlink account", {
                        description: errorMessage(err),
                      });
                    }
                  }}
                  onEdit={() => setEditModal(p)}
                  onDelete={() => setConfirmDelete(p)}
                />
              ))}
            </div>
            </>
          )}
        </div>

        {/* Quick Add Player — a persistent inline form, not a modal. */}
        <QuickAddPanel
          mutations={mutations}
          nameRef={quickAddNameRef}
          onAdded={() => toast.success("Player added")}
        />
      </div>

      {editModal ? (
        <PlayerModal
          player={editModal}
          mutations={mutations}
          onClose={() => setEditModal(null)}
        />
      ) : null}
      {/* Header "Link Account" → pick a player, then hand off to the
       * same LinkAccountModal the per-row link action uses. */}
      {linkPickerOpen ? (
        <LinkAccountPickerModal
          players={players}
          onPick={(player) => {
            setLinkPickerOpen(false);
            setLinkModal(player);
          }}
          onClose={() => setLinkPickerOpen(false)}
        />
      ) : null}
      {linkModal ? (
        <LinkAccountModal
          player={linkModal}
          mutations={mutations}
          onClose={() => setLinkModal(null)}
        />
      ) : null}
      {importOpen ? (
        <RosterImportModal onClose={() => setImportOpen(false)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Add Player — the always-visible inline sidebar form
// ---------------------------------------------------------------------------

/**
 * Persistent "Quick Add Player" form. Restores the legacy roster's
 * inline add panel — no modal, just a form pinned beside the table.
 *
 * Captures the fast common case: display name + jersey + position +
 * an optional family / self account link. After a successful add the
 * form resets and refocuses the name input so a coach can add
 * players in a quick run. Detailed edits (active toggle, etc.) still
 * go through `PlayerModal` per-row.
 *
 * The account link is a two-step write — `createPlayer` then
 * `linkAccount` — because the create endpoint has no link field. The
 * relationship defaults to `guardian` (the common case); the per-row
 * link modal is the place to pick `self` / `other`.
 */
function QuickAddPanel({
  mutations,
  nameRef,
  onAdded,
}: {
  mutations: ReturnType<typeof useRosterMutations>;
  /** The header "Add Player" button focuses this input. Mutable
   * `current` so the panel can refocus it after a successful add. */
  nameRef: React.MutableRefObject<HTMLInputElement | null>;
  onAdded: () => void;
}) {
  const toast = useToast();
  // The team's linkable users — fed to the "Link family / self
  // account" select. Always enabled: the panel is always mounted.
  const usersQuery = useCoachUsers(true);
  const linkableUsers = usersQuery.data?.users ?? [];
  const [name, setName] = useState("");
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [linkUserId, setLinkUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) {
      setError("Display name is required.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const created = await mutations.createPlayer({
        display_name: clean,
        jersey_number: jersey.trim(),
        // The roster has no `position` column — `notes` carries it.
        notes: position,
        active: true,
      });
      // Optional account link — a second write, since `createPlayer`
      // has no link field. A failed link must not lose the created
      // player, so it surfaces as its own toast and the add still
      // counts as a success.
      if (linkUserId) {
        try {
          await mutations.linkAccount({
            player_id: created.player.id,
            user_id: linkUserId,
            relationship: "guardian",
          });
        } catch (linkErr) {
          toast.error("Player added, but the account link failed", {
            description: errorMessage(linkErr),
          });
        }
      }
      onAdded();
      // Reset for the next quick add and refocus the name field.
      setName("");
      setJersey("");
      setPosition("");
      setLinkUserId("");
      nameRef.current?.focus();
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't add player", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <aside
      className="w-full shrink-0 xl:w-72"
      data-testid="roster-quick-add-panel"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 rounded-lg border border-rule bg-bg-card p-4"
      >
        <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
          Quick Add Player
        </h3>

        <QuickAddField label="Display name">
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jordan Reyes"
            data-testid="roster-quick-add-name"
            className="w-full bg-surface border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </QuickAddField>

        <QuickAddField label="Jersey number">
          <input
            type="text"
            value={jersey}
            onChange={(e) => setJersey(e.target.value)}
            placeholder="optional"
            data-testid="roster-quick-add-jersey"
            className="w-full bg-surface border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </QuickAddField>

        <QuickAddField label="Position" helper="optional">
          <TkticSelect
            value={position}
            onChange={setPosition}
            data-testid="roster-quick-add-position"
            size="sm"
            ariaLabel="Position"
            options={POSITION_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </QuickAddField>

        <QuickAddField label="Link family / self account" helper="optional">
          <TkticSelect
            value={linkUserId}
            onChange={setLinkUserId}
            data-testid="roster-quick-add-link"
            size="sm"
            ariaLabel="Link family / self account"
            disabled={usersQuery.isLoading}
            options={[
              {
                value: "",
                label: usersQuery.isLoading
                  ? "Loading accounts…"
                  : linkableUsers.length === 0
                    ? "— none —"
                    : "— none —",
              },
              ...linkableUsers.map((u) => ({
                value: u.id,
                label: `${u.display_name} · @${u.username}`,
              })),
            ]}
          />
        </QuickAddField>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-quick-add-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          data-testid="roster-quick-add-submit"
          className="inline-flex items-center justify-center gap-1.5 rounded bg-accent px-4 py-2 text-sm font-display uppercase tracking-wide text-white hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted transition-colors"
        >
          <PlusIcon />
          {submitting ? "Adding…" : "Add to Roster"}
        </button>

        <p className="rounded border border-rule bg-surface px-3 py-2 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wider text-accent">
            Tip
          </span>{" "}
          — Roster players are separate from login users. Linking a family
          account lets a parent see that player's private feedback. The link
          here is added as a guardian; use the row link action to set self /
          other.
        </p>
      </form>
    </aside>
  );
}

function QuickAddField(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
        {props.helper ? (
          <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
            ({props.helper})
          </span>
        ) : null}
      </span>
      {props.children}
    </label>
  );
}

function KpiTile(props: { label: string; value: string; delta?: string }) {
  return (
    <div className="rounded-lg border border-rule bg-bg-card px-4 py-3">
      <div className="text-ink-muted text-xs font-display uppercase tracking-wider">
        {props.label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <strong className="text-2xl font-display text-ink tabular-nums">
          {props.value}
        </strong>
        {props.delta ? (
          <span className="text-xs text-accent">{props.delta}</span>
        ) : null}
      </div>
    </div>
  );
}

type RosterRowProps = {
  player: Player;
  deleting: boolean;
  onAddObservation: () => void;
  /** Opens the M7 Player Development profile modal for this player. */
  onOpenDevelopment: () => void;
  /** Undefined ⇒ the goal button is hidden (Goals slice wires it). */
  onCreateGoal?: () => void;
  onLink: () => void;
  onUnlink: (linkId: number) => void;
  onEdit: () => void;
  onDelete: () => void;
};

function RosterRow({
  player,
  deleting,
  onAddObservation,
  onOpenDevelopment,
  onCreateGoal,
  onLink,
  onUnlink,
  onEdit,
  onDelete,
}: RosterRowProps) {
  const links = player.links ?? [];
  return (
    <tr
      className="border-b border-rule/50 last:border-b-0"
      data-testid="roster-row"
      data-player-id={player.id}
    >
      <td className="px-3 py-2 align-middle">
        <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-surface px-1 text-xs font-mono text-accent">
          {player.jersey_number || "—"}
        </span>
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="font-display text-ink">{player.display_name}</div>
        {player.notes?.trim() ? (
          <div className="text-ink-muted text-xs uppercase tracking-wide">
            {player.notes.trim()}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 align-middle">
        {links.length === 0 ? (
          <span className="text-ink-muted text-xs">No links</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => onUnlink(l.id)}
                data-testid="roster-link-chip"
                title={`Unlink @${l.username} — ${l.relationship}`}
                className="inline-flex items-center gap-1 rounded-full border border-rule bg-surface px-2 py-0.5 text-xs text-ink hover:border-destructive hover:text-destructive transition-colors"
              >
                <span className="text-ink-muted">{l.relationship}</span>
                <span>@{l.username}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-middle">
        <span
          className={[
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
            player.active
              ? "bg-good/15 text-good"
              : "bg-surface text-ink-muted",
          ].join(" ")}
        >
          <span
            aria-hidden="true"
            className={[
              "h-1.5 w-1.5 rounded-full",
              player.active ? "bg-good" : "bg-ink-muted",
            ].join(" ")}
          />
          {player.active ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-3 py-2 align-middle">
        {/* PR 8: per-player response signal aggregate. Renders a
         * compact chip strip + "Follow up" CTA when at least one
         * family-account reflection needs help. Coach clicks
         * "Open" to dive into the player's Development modal to
         * see the actual responses. */}
        <RosterResponseChips
          summary={player.signal_summary}
          onOpenDevelopment={onOpenDevelopment}
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <RosterActionButtons
          deleting={deleting}
          onAddObservation={onAddObservation}
          onOpenDevelopment={onOpenDevelopment}
          onCreateGoal={onCreateGoal}
          onLink={onLink}
          onEdit={onEdit}
          onDelete={onDelete}
          align="end"
        />
      </td>
    </tr>
  );
}

function RosterCard({
  player,
  deleting,
  onAddObservation,
  onOpenDevelopment,
  onCreateGoal,
  onLink,
  onUnlink,
  onEdit,
  onDelete,
}: RosterRowProps) {
  const links = player.links ?? [];
  return (
    <article
      className="rounded-lg border border-rule bg-bg-card p-3"
      data-testid="roster-card"
      data-player-id={player.id}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-surface px-1 text-sm font-mono text-accent">
          {player.jersey_number || "—"}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-ink">{player.display_name}</div>
          {player.notes?.trim() ? (
            <div className="text-ink-muted text-xs uppercase tracking-wide">
              {player.notes.trim()}
            </div>
          ) : null}
          <span
            className={[
              "mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
              player.active
                ? "bg-good/15 text-good"
                : "bg-surface text-ink-muted",
            ].join(" ")}
          >
            <span
              aria-hidden="true"
              className={[
                "h-1.5 w-1.5 rounded-full",
                player.active ? "bg-good" : "bg-ink-muted",
              ].join(" ")}
            />
            {player.active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="mt-3">
        {links.length === 0 ? (
          <span className="text-ink-muted text-xs">No linked accounts</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => onUnlink(l.id)}
                data-testid="roster-card-link-chip"
                title={`Unlink @${l.username} — ${l.relationship}`}
                className="inline-flex items-center gap-1 rounded-full border border-rule bg-surface px-2 py-0.5 text-xs text-ink hover:border-destructive hover:text-destructive transition-colors"
              >
                <span className="text-ink-muted">{l.relationship}</span>
                <span>@{l.username}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 overflow-x-auto border-t border-rule/60 pt-2">
        <RosterActionButtons
          deleting={deleting}
          onAddObservation={onAddObservation}
          onOpenDevelopment={onOpenDevelopment}
          onCreateGoal={onCreateGoal}
          onLink={onLink}
          onEdit={onEdit}
          onDelete={onDelete}
          align="start"
        />
      </div>
    </article>
  );
}

function RosterActionButtons({
  deleting,
  onAddObservation,
  onOpenDevelopment,
  onCreateGoal,
  onLink,
  onEdit,
  onDelete,
  align,
}: Omit<RosterRowProps, "player" | "onUnlink"> & { align: "start" | "end" }) {
  return (
    <div
      className={[
        "inline-flex w-max flex-nowrap items-center gap-0.5 rounded-md border border-rule bg-surface p-0.5",
        align === "end" ? "justify-end" : "justify-start",
      ].join(" ")}
    >
      <IconButton
        label="Add observation"
        testid="roster-action-observation"
        onClick={onAddObservation}
        icon={<ObservationIcon />}
      />
      <IconButton
        label="Development profile"
        testid="roster-action-development"
        onClick={onOpenDevelopment}
        icon={<ChartIcon />}
      />
      {onCreateGoal ? (
        <IconButton
          label="Create goal"
          testid="roster-action-goal"
          onClick={onCreateGoal}
          icon={<GoalIcon />}
        />
      ) : null}
      <IconButton
        label="Link account"
        testid="roster-action-link"
        onClick={onLink}
        icon={<LinkIcon />}
      />
      <IconButton
        label="Edit"
        testid="roster-action-edit"
        onClick={onEdit}
        icon={<EditIcon />}
      />
      <IconButton
        label="Delete"
        testid="roster-action-delete"
        onClick={onDelete}
        disabled={deleting}
        danger
        icon={<TrashIcon />}
      />
    </div>
  );
}

/** Compact square icon button for a roster row action. The text
 * label is exposed via `title` + `aria-label` so the icon-only
 * control stays accessible and discoverable on hover. */
function IconButton(props: {
  label: string;
  testid: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive hover:bg-destructive/10"
          : "border-rule text-ink-muted hover:border-accent hover:text-accent hover:bg-accent/10",
      ].join(" ")}
    >
      {props.icon}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons — the app has no icon library; surfaces draw
// their own (cf. `ChevronIcon` in `TkticSelect`). All 16×16,
// `currentColor` stroke so they inherit the button's text color.
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function PlusIcon() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function ObservationIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="6" y1="20" x2="6" y2="12" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="18" y1="20" x2="18" y2="14" />
    </svg>
  );
}

function GoalIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...ICON_PROPS}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// PR 8: Roster-row response signal chips
// ---------------------------------------------------------------------------

/** Compact chip strip showing per-player "How'd it land?" reaction
 * counts on the Roster row (PR 8). Mirrors the mockup's Phase 6
 * coach roster (`⚡ 2 ✓ 1 · ? 1 needs help · 1 no response`).
 *
 * When the player has at least one `need_help` reaction, the row
 * surfaces a small orange "Follow up" CTA that opens the Development
 * modal so the coach can see the actual reflection bodies.
 *
 * Hides entirely (renders an em-dash placeholder) when no linked
 * account has responded — avoids a noisy "0 0 0" tile on rosters
 * with mostly self-account players. */
function RosterResponseChips({
  summary,
  onOpenDevelopment,
}: {
  summary?: { got_it: number; need_help: number; tried_this: number };
  onOpenDevelopment: () => void;
}) {
  const got = summary?.got_it ?? 0;
  const need = summary?.need_help ?? 0;
  const tried = summary?.tried_this ?? 0;
  const total = got + need + tried;
  if (total === 0) {
    return (
      <span
        className="text-ink-muted text-xs"
        data-testid="roster-response-chips-empty"
      >
        —
      </span>
    );
  }
  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-testid="roster-response-chips"
    >
      {got > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-good/15 text-good px-1.5 py-0.5 text-[0.65rem] font-mono"
          title="Got it"
          data-signal="got_it"
        >
          <span aria-hidden="true">✓</span>
          {got}
        </span>
      ) : null}
      {tried > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-accent/15 text-accent-ink px-1.5 py-0.5 text-[0.65rem] font-mono"
          title="I tried this"
          data-signal="tried_this"
        >
          <span aria-hidden="true">⚡</span>
          {tried}
        </span>
      ) : null}
      {need > 0 ? (
        <button
          type="button"
          onClick={onOpenDevelopment}
          className="inline-flex items-center gap-0.5 rounded-full bg-flag text-navy px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider hover:opacity-90 transition-opacity"
          title={`${need} needs follow-up — open the player's Development modal`}
          data-testid="roster-response-follow-up"
          data-signal="need_help"
        >
          <span aria-hidden="true">?</span>
          {need} follow up
        </button>
      ) : null}
    </div>
  );
}
